import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatErr, rupiah } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { EventForm } from "@/pages/PhotographerDashboard";
import { ShareButton } from "@/components/ShareButton";
import { UploadPanel } from "@/components/UploadPanel";
import { PhotoManager } from "@/components/PhotoManager";
import { QueueStatus } from "@/components/QueueStatus";

export default function PhotographerEvent() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => api.get(`/events/${id}`, { params: { limit: 1 } }).then((r) => setEvent(r.data.event));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { setReloadKey((k) => k + 1); load(); };
  const [pingKey, setPingKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const onReadyChange = useCallback(() => setReloadKey((k) => k + 1), []);

  const save = async (body) => {
    try {
      await api.put(`/events/${id}`, body);
      setEditOpen(false);
      toast.success(body.apply_price_to_all ? "Event & harga semua foto diperbarui" : "Event diperbarui");
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const removeEvent = async () => {
    if (!window.confirm("Hapus event beserta semua fotonya?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Event dihapus");
    nav("/fotografer");
  };

  if (!event) return <p className="p-16 text-zinc-500">Memuat...</p>;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label={`${event.kind === "non_event" ? "Non Event" : "Event"}${event.location ? ` · ${event.location}` : ""} · ${rupiah(event.price)} / foto`} title={event.title}>
        <div className="flex flex-wrap gap-2">
          <ShareButton event={event} />
          <Button asChild variant="outline" className="rounded-xl h-11 bg-white" data-testid="event-packages-link"><Link to={`/fotografer/paket?event=${event.id}`}><PackageCheck className="mr-2 h-4 w-4" />Paket</Link></Button>
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-xl h-11 bg-white" data-testid="edit-event-button"><Pencil className="mr-2 h-4 w-4" />Edit</Button></DialogTrigger>
            <DialogContent className="rounded-2xl" aria-describedby={undefined}><DialogHeader><DialogTitle>Edit event</DialogTitle></DialogHeader>
              <EventForm editing initial={{ kind: event.kind, title: event.title, description: event.description, location: event.location, date: event.date, price: event.price }} onSubmit={save} submitLabel="Simpan" />
            </DialogContent>
          </Dialog>
          <Button variant="outline" className="rounded-xl h-11 bg-white text-rose-600" onClick={removeEvent} data-testid="delete-event-button"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </PageHeader>
      <UploadPanel eventId={id} onDone={() => setPingKey((k) => k + 1)} onActiveChange={setUploading} />
      <QueueStatus eventId={id} pingKey={pingKey} onReadyChange={onReadyChange} uploading={uploading} />
      <UploadPanel eventId={id} mode="hd" onDone={() => setReloadKey((k) => k + 1)} />
      <PhotoManager eventId={id} reloadKey={reloadKey} onChanged={load} />
    </main>
  );
}
